import React, { useState, useRef, useEffect, useCallback, memo } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import logo from "../../assets/images/LogoDeko.png";
import LanguageSwitcher from "../../pages/LanguageSwitcher";
import "./Header.css";
import { useTranslation } from "react-i18next";

// Catálogo dinámico
import { CATEGORY_TREE } from "../../pages/products";
import { listBySubcategory, listByCategory } from "../../pages/products";

/**
 * Header responsive con:
 * - Desktop: menú horizontal tradicional con dropdown para productos
 * - Mobile: Sheet superior con acordeón para productos
 * - Sticky + blur on scroll
 * - Accesibilidad completa
 */
const Header = ({ overDarkHero = false }) => {
  const { t, i18n } = useTranslation();
  const lang = (i18n?.language || "es").startsWith("en") ? "en" : "es";

  const [sheetOpen, setSheetOpen] = useState(false);
  const [productsOpenMobile, setProductsOpenMobile] = useState(false);
  const [productsOpenDesktop, setProductsOpenDesktop] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const headerRef = useRef(null);
  const sheetRef = useRef(null);
  const accordionRef = useRef(null);
  const toggleBtnRef = useRef(null);
  const desktopDropdownRef = useRef(null);
  const desktopProductsBtnRef = useRef(null);

  const location = useLocation();
  const navigate = useNavigate();

  // ===== Helpers =====
  const bodyLock = useCallback((lock) => {
    const m = lock ? "add" : "remove";
    document.body.classList[m]("menu-open");
    document.documentElement.classList[m]("menu-open");
  }, []);

  const closeSheet = useCallback(() => {
    setSheetOpen(false);
    setProductsOpenMobile(false);
    bodyLock(false);
    toggleBtnRef.current?.focus();
  }, [bodyLock]);

  const toggleSheet = useCallback(() => {
    setSheetOpen((prev) => {
      const next = !prev;
      bodyLock(next);
      if (!next) setProductsOpenMobile(false);
      return next;
    });
  }, [bodyLock]);

  const toggleProductsMobile = useCallback(() => {
    setProductsOpenMobile((v) => !v);
  }, []);

  const toggleProductsDesktop = useCallback((open) => {
    setProductsOpenDesktop(open);
  }, []);

  // ===== Helpers de CATEGORY_TREE (soporta strings viejos y objetos nuevos) =====
  const getSubKey = useCallback((sub) => {
    if (!sub) return "";
    if (typeof sub === "string") return sub; // compat con tu versión vieja
    if (typeof sub === "object") return sub.key ?? sub.es ?? sub.en ?? "";
    return String(sub);
  }, []);

  const getSubLabel = useCallback(
    (sub) => {
      if (!sub) return "";
      if (typeof sub === "string") return sub; // compat
      if (typeof sub === "object") return sub[lang] ?? sub.es ?? sub.en ?? "";
      return String(sub);
    },
    [lang]
  );

  // ===== Navegación inteligente por categoría =====
  const goToCategory = useCallback(
    (cat, subKey) => {
      if (cat && subKey) {
        const matches = listBySubcategory?.(cat, subKey) ?? [];
        if (matches.length === 1) {
          closeSheet();
          setProductsOpenDesktop(false);
          return navigate(`/producto/${matches[0].slug}`);
        }
      }

      if (cat && !subKey) {
        const matches = listByCategory?.(cat) ?? [];
        if (matches.length === 1) {
          closeSheet();
          setProductsOpenDesktop(false);
          return navigate(`/producto/${matches[0].slug}`);
        }
      }

      const q = new URLSearchParams();
      if (cat) q.set("cat", cat);
      if (subKey) q.set("sub", subKey);

      closeSheet();
      setProductsOpenDesktop(false);
      navigate(`/catalogo?${q.toString()}`);
    },
    [closeSheet, navigate]
  );

  // ===== Sticky + blur al hacer scroll > 8px =====
  useEffect(() => {
    const onScroll = () => {
      const isScrolled = window.scrollY > 8;
      setScrolled(isScrolled);
      headerRef.current?.classList.toggle("header--scrolled", isScrolled);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // ===== Cierre en cambio de ruta =====
  useEffect(() => {
    closeSheet();
    setProductsOpenDesktop(false);
  }, [location.pathname, location.search, closeSheet]);

  // ===== Accesibilidad: ESC para cerrar =====
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (sheetOpen) {
          e.preventDefault();
          closeSheet();
        } else if (productsOpenDesktop) {
          e.preventDefault();
          setProductsOpenDesktop(false);
          desktopProductsBtnRef.current?.focus();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [sheetOpen, productsOpenDesktop, closeSheet]);

  // ===== Focus trap dentro del sheet mobile =====
  useEffect(() => {
    if (!sheetOpen) return;

    const container = sheetRef.current;
    if (!container) return;

    const focusables = container.querySelectorAll(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"]), select, input, textarea'
    );
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    first?.focus();

    const handleTab = (e) => {
      if (e.key !== "Tab") return;
      if (focusables.length === 0) return;

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    container.addEventListener("keydown", handleTab);
    return () => container.removeEventListener("keydown", handleTab);
  }, [sheetOpen]);

  // ===== Cerrar dropdown desktop al hacer click fuera =====
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        desktopDropdownRef.current &&
        !desktopDropdownRef.current.contains(e.target) &&
        desktopProductsBtnRef.current &&
        !desktopProductsBtnRef.current.contains(e.target)
      ) {
        setProductsOpenDesktop(false);
      }
    };

    if (productsOpenDesktop) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [productsOpenDesktop]);

  // ===== Animación de altura del acordeón mobile =====
  const [accHeight, setAccHeight] = useState(0);
  useEffect(() => {
    const el = accordionRef.current;
    if (!el) return;
    if (productsOpenMobile) {
      const h = el.scrollHeight;
      setAccHeight(h);
    } else {
      setAccHeight(0);
    }
  }, [productsOpenMobile]);

  const linkClassMobile = useCallback(
    ({ isActive }) =>
      isActive ? "nav-mobile__link nav-mobile__link--active" : "nav-mobile__link",
    []
  );

  const linkClassDesktop = useCallback(
    ({ isActive }) =>
      isActive ? "nav-desktop__link nav-desktop__link--active" : "nav-desktop__link",
    []
  );

  const openLabel = t("header.a11y.openMenu", "Abrir menú");
  const closeLabel = t("header.a11y.closeMenu", "Cerrar menú");

  return (
    <header
      className={`header ${overDarkHero ? "header--over-dark" : ""} ${
        scrolled ? "header--scrolled" : ""
      }`}
      ref={headerRef}
    >
      <div className="container header__inner">
        {/* Logo */}
        <Link
          to="/"
          className="logo"
          onClick={() => {
            closeSheet();
            setProductsOpenDesktop(false);
          }}
        >
          <img src={logo} className="logo__img" alt={t("header.brandAlt", "DEKO")} />
        </Link>

        {/* ===== Navegación Desktop ===== */}
        <nav className="nav-desktop" aria-label={t("header.navMain", "Navegación principal")}>
          <ul className="nav-desktop__menu">
            <li className="nav-desktop__item">
              <NavLink to="/" end className={linkClassDesktop}>
                {t("header.links.home", "Inicio")}
              </NavLink>
            </li>

            <li className={`nav-desktop__item ${productsOpenDesktop ? "nav-desktop__item--open" : ""}`}>
              <button
                ref={desktopProductsBtnRef}
                className="nav-desktop__link"
                onClick={() => toggleProductsDesktop(!productsOpenDesktop)}
                onMouseEnter={() => toggleProductsDesktop(true)}
                aria-expanded={productsOpenDesktop}
                aria-haspopup="true"
                type="button"
              >
                {t("header.links.products", "Productos")}{" "}
                <span className="nav-desktop__caret" aria-hidden>
                  ▾
                </span>
              </button>

              {/* Dropdown Desktop */}
              <div
                ref={desktopDropdownRef}
                className={`dropdown-desktop ${productsOpenDesktop ? "dropdown-desktop--open" : ""}`}
                onMouseLeave={() => toggleProductsDesktop(false)}
              >
                <div className="dropdown-desktop__head">
                  <h4>{t("header.dropdown.title", "Catálogo")}</h4>
                  <p>{t("header.dropdown.subtitle", "Explora nuestras líneas por material")}</p>
                </div>

                <div className="dropdown-desktop__grid">
                  {CATEGORY_TREE?.WPC && (
                    <div className="dropdown-desktop__group">
                      <div className="dropdown-desktop__title">{t("header.categories.wpc", "WPC")}</div>
                      <ul className="dropdown-desktop__list">
                        {CATEGORY_TREE.WPC.map((sub) => {
                          const subKey = getSubKey(sub);
                          const subLabel = getSubLabel(sub);
                          return (
                            <li key={`wpc-${subKey}`}>
                              <button
                                className="dropdown-desktop__link"
                                onClick={() => goToCategory("WPC", subKey)}
                              >
                                <span>{subLabel}</span>
                                <span className="arrow" aria-hidden>
                                  →
                                </span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}

                  {CATEGORY_TREE?.PVC && (
                    <div className="dropdown-desktop__group">
                      <div className="dropdown-desktop__title">{t("header.categories.pvc", "PVC")}</div>
                      <ul className="dropdown-desktop__list">
                        {CATEGORY_TREE.PVC.map((sub) => {
                          const subKey = getSubKey(sub);
                          const subLabel = getSubLabel(sub);
                          return (
                            <li key={`pvc-${subKey}`}>
                              <button
                                className="dropdown-desktop__link"
                                onClick={() => goToCategory("PVC", subKey)}
                              >
                                <span>{subLabel}</span>
                                <span className="arrow" aria-hidden>
                                  →
                                </span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}

                  {CATEGORY_TREE?.SPC && (
                    <div className="dropdown-desktop__group">
                      <div className="dropdown-desktop__title">{t("header.categories.spc", "SPC")}</div>
                      <ul className="dropdown-desktop__list">
                        {CATEGORY_TREE.SPC.map((sub) => {
                          const subKey = getSubKey(sub);
                          const subLabel = getSubLabel(sub);
                          return (
                            <li key={`spc-${subKey}`}>
                              <button
                                className="dropdown-desktop__link"
                                onClick={() => goToCategory("SPC", subKey)}
                              >
                                <span>{subLabel}</span>
                                <span className="arrow" aria-hidden>
                                  →
                                </span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}

                  {CATEGORY_TREE?.DEKOWALL && (
                    <div className="dropdown-desktop__group">
                      <div className="dropdown-desktop__title">
                        {t("header.categories.dekowall", "DEKOWALL")}
                      </div>
                      <ul className="dropdown-desktop__list">
                        {CATEGORY_TREE.DEKOWALL.map((sub) => {
                          const subKey = getSubKey(sub);
                          const subLabel = getSubLabel(sub);
                          return (
                            <li key={`dekowall-${subKey}`}>
                              <button
                                className="dropdown-desktop__link"
                                onClick={() => goToCategory("DEKOWALL", subKey)}
                              >
                                <span>{subLabel}</span>
                                <span className="arrow" aria-hidden>
                                  →
                                </span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}

                  {CATEGORY_TREE?.DEKOROOF && (
                    <div className="dropdown-desktop__group">
                      <div className="dropdown-desktop__title">
                        {t("header.categories.dekoroof", "DEKOROOF")}
                      </div>
                      <ul className="dropdown-desktop__list">
                        {CATEGORY_TREE.DEKOROOF.map((sub) => {
                          const subKey = getSubKey(sub);
                          const subLabel = getSubLabel(sub);
                          return (
                            <li key={`dekoroof-${subKey}`}>
                              <button
                                className="dropdown-desktop__link"
                                onClick={() => goToCategory("DEKOROOF", subKey)}
                              >
                                <span>{subLabel}</span>
                                <span className="arrow" aria-hidden>
                                  →
                                </span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </li>

            <li className="nav-desktop__item">
              <NavLink to="/contacto" className={linkClassDesktop}>
                {t("header.links.contact", "Contacto")}
              </NavLink>
            </li>
          </ul>
        </nav>

        {/* Idiomas */}
        <div className="header__right" aria-label={t("header.langSelectorAria", "Selector de idioma")}>
          <div className="lang">
            <LanguageSwitcher />
          </div>
        </div>

        {/* Hamburguesa (solo mobile) */}
        <button
          ref={toggleBtnRef}
          className={`hamb ${sheetOpen ? "hamb--open" : ""}`}
          onClick={toggleSheet}
          aria-label={sheetOpen ? closeLabel : openLabel}
          aria-expanded={sheetOpen}
          aria-pressed={sheetOpen}
          aria-controls="mobile-sheet"
          type="button"
        >
          <span />
          <span />
          <span />
        </button>
      </div>

      {/* ===== Sheet / Drawer superior (solo mobile) ===== */}
      <div
        id="mobile-sheet"
        className={`nav-sheet ${sheetOpen ? "nav-sheet--open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={t("header.sheet.aria", "Menú de navegación")}
      >
        <div className="nav-sheet__overlay" onClick={closeSheet} aria-hidden="true" role="presentation" />
        <nav className="nav-mobile" ref={sheetRef} aria-label={t("header.navMain", "Navegación principal")}>
          <NavLink to="/" end className={linkClassMobile} onClick={closeSheet}>
            {t("header.links.home", "Inicio")}
          </NavLink>

          {/* Acordeón: Productos Mobile */}
          <div className="nav-mobile__dropdown">
            <button
              className={`nav-mobile__btn ${productsOpenMobile ? "nav-mobile__btn--open" : ""}`}
              onClick={toggleProductsMobile}
              aria-expanded={productsOpenMobile}
              aria-controls="productos-panel-mobile"
              type="button"
            >
              {t("header.links.products", "Productos")}{" "}
              <span className="nav-mobile__caret" aria-hidden>
                ▾
              </span>
            </button>

            <div className="accordion">
              <div
                id="productos-panel-mobile"
                className={`accordion__content ${productsOpenMobile ? "accordion__content--open" : ""}`}
                ref={accordionRef}
                style={{ height: `${accHeight}px` }}
              >
                <div className="dropdown-mobile__head">
                  <h4>{t("header.dropdown.title", "Catálogo")}</h4>
                  <p>{t("header.dropdown.subtitle", "Explora nuestras líneas por material")}</p>
                </div>

                <div className="dropdown-mobile__grid">
                  {CATEGORY_TREE?.WPC && (
                    <div className="group-mobile">
                      <div className="group-mobile__title">{t("header.categories.wpc", "WPC")}</div>
                      <ul className="group-mobile__list">
                        {CATEGORY_TREE.WPC.map((sub) => {
                          const subKey = getSubKey(sub);
                          const subLabel = getSubLabel(sub);
                          return (
                            <li key={`wpc-${subKey}`}>
                              <button className="group-mobile__link" onClick={() => goToCategory("WPC", subKey)}>
                                <span>{subLabel}</span>
                                <span className="arrow" aria-hidden>
                                  →
                                </span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}

                  {CATEGORY_TREE?.PVC && (
                    <div className="group-mobile">
                      <div className="group-mobile__title">{t("header.categories.pvc", "PVC")}</div>
                      <ul className="group-mobile__list">
                        {CATEGORY_TREE.PVC.map((sub) => {
                          const subKey = getSubKey(sub);
                          const subLabel = getSubLabel(sub);
                          return (
                            <li key={`pvc-${subKey}`}>
                              <button className="group-mobile__link" onClick={() => goToCategory("PVC", subKey)}>
                                <span>{subLabel}</span>
                                <span className="arrow" aria-hidden>
                                  →
                                </span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}

                  {CATEGORY_TREE?.SPC && (
                    <div className="group-mobile">
                      <div className="group-mobile__title">{t("header.categories.spc", "SPC")}</div>
                      <ul className="group-mobile__list">
                        {CATEGORY_TREE.SPC.map((sub) => {
                          const subKey = getSubKey(sub);
                          const subLabel = getSubLabel(sub);
                          return (
                            <li key={`spc-${subKey}`}>
                              <button className="group-mobile__link" onClick={() => goToCategory("SPC", subKey)}>
                                <span>{subLabel}</span>
                                <span className="arrow" aria-hidden>
                                  →
                                </span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}

                  {CATEGORY_TREE?.DEKOWALL && (
                    <div className="group-mobile">
                      <div className="group-mobile__title">{t("header.categories.dekowall", "DEKOWALL")}</div>
                      <ul className="group-mobile__list">
                        {CATEGORY_TREE.DEKOWALL.map((sub) => {
                          const subKey = getSubKey(sub);
                          const subLabel = getSubLabel(sub);
                          return (
                            <li key={`dekowall-${subKey}`}>
                              <button
                                className="group-mobile__link"
                                onClick={() => goToCategory("DEKOWALL", subKey)}
                              >
                                <span>{subLabel}</span>
                                <span className="arrow" aria-hidden>
                                  →
                                </span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}

                  {/* ✅ TE FALTABA EN MOBILE */}
                  {CATEGORY_TREE?.DEKOROOF && (
                    <div className="group-mobile">
                      <div className="group-mobile__title">{t("header.categories.dekoroof", "DEKOROOF")}</div>
                      <ul className="group-mobile__list">
                        {CATEGORY_TREE.DEKOROOF.map((sub) => {
                          const subKey = getSubKey(sub);
                          const subLabel = getSubLabel(sub);
                          return (
                            <li key={`dekoroof-${subKey}`}>
                              <button
                                className="group-mobile__link"
                                onClick={() => goToCategory("DEKOROOF", subKey)}
                              >
                                <span>{subLabel}</span>
                                <span className="arrow" aria-hidden>
                                  →
                                </span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <NavLink to="/contacto" className={linkClassMobile} onClick={closeSheet}>
            {t("header.links.contact", "Contacto")}
          </NavLink>

          {/* Footer fijo con CTA Mobile */}
          <div className="nav-mobile__footer">
            <a
              className="nav-mobile__cta"
              href="https://wa.me/50661686664"
              target="_blank"
              rel="noopener noreferrer"
              onClick={closeSheet}
            >
              {t("header.mobile.cta", "Contáctanos por WhatsApp")}
            </a>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default memo(Header);
